/** Cards (L): collection book, six-card deck in a hexagon, awakening and set bonuses. */
import { useState } from 'preact/hooks';
import { CARDS, CARD_DECK_SIZE, CARD_SETS, type CardDef, MAX_AWAKEN, collectionHpBonus } from '../../game/build';
import { RARITY_COLOR } from '../../game/items';
import { Window } from '../../ui/app/Window';
import { type Ctx, Pips, commit, hueCss, notify, useCtx } from './common';
import { keyTrap } from './keys';
import { type CardView, RARITY_NAME, cardSetProgress, cardViews, fmtPct, initials } from './logic';
import { hideTip, tip } from './Tooltip';

const CARD_DRAG = 'application/x-af-card';
const SLOT_DRAG = 'application/x-af-deckslot';

/** Procedural portrait: rarity frame, hue gradient, bust silhouette and initials. */
export function CardArt({ def, owned = true, size = 'md' }: { def: CardDef; owned?: boolean; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span class={`bcd-art bcd-art--${size} ${owned ? '' : 'is-unowned'}`} style={{ '--h': hueCss(def.hue), '--f': RARITY_COLOR[def.rarity] }}>
      <span class="bcd-art-bg" />
      <svg class="bcd-bust" viewBox="0 0 60 84" aria-hidden="true">
        <path d="M30 14.5 a10.5 11.5 0 1 0 0.01 0 Z M8 84 C8 60 17 47 30 47 C43 47 52 60 52 84 Z" />
      </svg>
      <span class="bcd-ini">{owned ? initials(def.name) : '?'}</span>
    </span>
  );
}

function CardTip({ v }: { v: CardView }) {
  const set = CARD_SETS[v.def.set];
  return (
    <div class="btt-item">
      <div class={`btt-head btt-head--${v.def.rarity}`}>
        <span class={`btt-name r-${v.def.rarity}`}>{v.def.name}</span>
      </div>
      <div class="btt-sec">
        <div class={`r-${v.def.rarity}`}>{RARITY_NAME[v.def.rarity]} Card</div>
        <div class="btt-dim">Set: {set?.name ?? v.def.set}</div>
        {v.owned ? (
          <>
            <div class="btt-line">
              <span>Awakening</span>
              <b class="btt-gold">
                {v.awaken} / {MAX_AWAKEN}
              </b>
            </div>
            <div class="btt-line">
              <span>Duplicates</span>
              <b>{v.need > 0 ? `${v.dupes} / ${v.need} for next` : v.dupes}</b>
            </div>
          </>
        ) : (
          <div class="btt-dim">Not yet collected.</div>
        )}
      </div>
    </div>
  );
}

function place(ctx: Ctx, slot: number, id: string | null): void {
  ctx.p.setDeckCard(slot, id);
  commit();
}

function Collection({ views, sel, setSel, onPlace }: { views: CardView[]; sel: string | null; setSel: (id: string | null) => void; onPlace: (id: string) => void }) {
  return (
    <div class="bcd-grid" role="listbox" aria-label="Card collection">
      {views.map((v) => (
        <button
          key={v.def.id}
          type="button"
          role="option"
          aria-selected={sel === v.def.id}
          class={`bcd-card ${v.owned ? '' : 'is-unowned'} ${sel === v.def.id ? 'is-sel' : ''} ${v.deckIndex >= 0 ? 'is-deck' : ''}`}
          draggable={v.owned}
          onDragStart={(ev) => {
            hideTip();
            ev.dataTransfer?.setData(CARD_DRAG, v.def.id);
          }}
          onClick={() => setSel(sel === v.def.id ? null : v.def.id)}
          onDblClick={() => v.owned && onPlace(v.def.id)}
          {...tip(() => <CardTip v={v} />)}
        >
          <span class="bcd-frame">
            <CardArt def={v.def} owned={v.owned} />
            {v.deckIndex >= 0 && <span class="bcd-in">Deck</span>}
            {v.dupes > 0 && <span class={`bcd-dupes ${v.canAwaken ? 'is-ready' : ''}`}>×{v.dupes}</span>}
          </span>
          <span class={`bcd-name ${v.owned ? `r-${v.def.rarity}` : ''}`}>{v.def.name}</span>
          <Pips n={v.awaken} max={MAX_AWAKEN} class="bcd-pips" />
        </button>
      ))}
    </div>
  );
}

function Deck({ ctx, selCard, selSlot, setSelSlot, setSelCard }: { ctx: Ctx; selCard: CardView | null; selSlot: number | null; setSelSlot: (i: number | null) => void; setSelCard: (id: string | null) => void }) {
  const col = ctx.a.cards;
  const [over, setOver] = useState(-1);
  const cx = 180;
  const cy = 186;
  const R = 122;
  const pos = Array.from({ length: CARD_DECK_SIZE }, (_, i) => {
    const ang = -Math.PI / 2 + (i * Math.PI * 2) / CARD_DECK_SIZE;
    return { x: cx + Math.cos(ang) * R, y: cy + Math.sin(ang) * R };
  });
  const total = col.deck.reduce((n, id) => n + (id ? (col.owned[id] ?? 0) : 0), 0);
  return (
    <div class="bcd-deck">
      <svg class="bcd-deck-svg" viewBox="0 0 360 372" aria-hidden="true">
        <defs>
          <filter id="bcdGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.2" />
          </filter>
        </defs>
        {pos.map((p, i) => {
          const q = pos[(i + 1) % pos.length]!;
          const on = !!col.deck[i] && !!col.deck[(i + 1) % pos.length];
          return (
            <g key={i}>
              <line x1={cx} y1={cy} x2={p.x} y2={p.y} class={`bcd-spoke ${col.deck[i] ? 'is-on' : ''}`} />
              {on && <line x1={p.x} y1={p.y} x2={q.x} y2={q.y} class="bcd-link-glow" filter="url(#bcdGlow)" />}
              <line x1={p.x} y1={p.y} x2={q.x} y2={q.y} class={`bcd-link ${on ? 'is-on' : ''}`} />
            </g>
          );
        })}
      </svg>
      <div class="bcd-core" {...tip(() => <div class="btt-note">Total awakening of the cards in your deck. Set bonuses unlock at awakening thresholds.</div>)}>
        <small>Awakening</small>
        <b>{total}</b>
      </div>
      {pos.map((p, i) => {
        const id = col.deck[i] ?? null;
        const def = id ? CARDS[id] : undefined;
        return (
          <button
            key={i}
            type="button"
            class={`bcd-slot ${def ? '' : 'is-empty'} ${selSlot === i ? 'is-sel' : ''} ${over === i ? 'is-over' : ''} ${selCard?.owned && selCard.deckIndex < 0 ? 'is-target' : ''}`}
            style={{ left: `${(p.x / 360) * 100}%`, top: `${(p.y / 372) * 100}%` }}
            aria-label={def ? `Deck slot ${i + 1}: ${def.name}` : `Deck slot ${i + 1} (empty)`}
            draggable={!!def}
            onDragStart={(ev) => {
              hideTip();
              ev.dataTransfer?.setData(SLOT_DRAG, String(i));
            }}
            onDragOver={(ev) => {
              const t = ev.dataTransfer?.types ?? [];
              if (t.includes(CARD_DRAG) || t.includes(SLOT_DRAG)) {
                ev.preventDefault();
                setOver(i);
              }
            }}
            onDragLeave={() => setOver(-1)}
            onDrop={(ev) => {
              ev.preventDefault();
              setOver(-1);
              const cid = ev.dataTransfer?.getData(CARD_DRAG);
              const from = ev.dataTransfer?.getData(SLOT_DRAG);
              if (cid) place(ctx, i, cid);
              else if (from) {
                const a = Number(from);
                const moving = col.deck[a] ?? null;
                const here = col.deck[i] ?? null;
                if (moving && a !== i) {
                  ctx.p.setDeckCard(i, moving);
                  ctx.p.setDeckCard(a, here);
                  commit();
                }
              }
            }}
            onClick={() => {
              if (selCard?.owned && selCard.deckIndex !== i) {
                place(ctx, i, selCard.def.id);
                setSelCard(null);
              } else {
                setSelSlot(selSlot === i ? null : i);
                if (id) setSelCard(id);
              }
            }}
            onContextMenu={(ev) => {
              ev.preventDefault();
              if (!id) return;
              hideTip();
              place(ctx, i, null);
            }}
            {...(def ? tip(() => <CardTip v={cardViews(col).find((v) => v.def.id === def.id)!} />) : {})}
          >
            {def ? (
              <>
                <CardArt def={def} size="sm" />
                <Pips n={col.owned[def.id] ?? 0} max={MAX_AWAKEN} class="bcd-pips" />
              </>
            ) : (
              <span class="bcd-slot-plus">+</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Detail({ ctx, v }: { ctx: Ctx; v: CardView | null }) {
  if (!v) {
    return (
      <div class="bcd-detail is-empty">
        <p>Select a card to see its details. Double-click or drag an owned card onto the deck; right-click a deck card to remove it.</p>
      </div>
    );
  }
  const col = ctx.a.cards;
  const inDeck = v.deckIndex >= 0;
  const addToDeck = () => {
    const empty = col.deck.findIndex((d) => !d);
    if (empty < 0) {
      notify('Your deck is full — right-click a deck card to remove it first.', '#e5a24d');
      return;
    }
    place(ctx, empty, v.def.id);
  };
  return (
    <div class="bcd-detail">
      <CardArt def={v.def} owned={v.owned} size="lg" />
      <div class="bcd-detail-info">
        <div class={`bcd-detail-name r-${v.def.rarity}`}>{v.def.name}</div>
        <div class="bcd-detail-sub">
          {RARITY_NAME[v.def.rarity]} · {CARD_SETS[v.def.set]?.name ?? v.def.set}
        </div>
        {v.owned ? (
          <>
            <div class="bcd-detail-aw">
              <Pips n={v.awaken} max={MAX_AWAKEN} class="bcd-pips bcd-pips--lg" />
              <span>
                Awakening {v.awaken} / {MAX_AWAKEN}
              </span>
            </div>
            <div class="bcd-detail-dupes">{v.need > 0 ? `Duplicates ${v.dupes} / ${v.need} needed for the next awakening` : `Fully awakened${v.dupes ? ` · ${v.dupes} spare duplicates` : ''}`}</div>
          </>
        ) : (
          <div class="bcd-detail-dupes muted">Not collected yet. Cards drop from bosses, elites and chests.</div>
        )}
      </div>
      {v.owned && (
        <div class="bcd-detail-btns">
          <button
            type="button"
            class="btn btn--gold"
            disabled={!v.canAwaken}
            onClick={() => {
              if (ctx.p.awaken(v.def.id)) {
                notify(`${v.def.name} awakened to ${(col.owned[v.def.id] ?? 0).toString()}`, '#ffd070');
                commit();
              }
            }}
          >
            Awaken
          </button>
          {inDeck ? (
            <button type="button" class="btn" onClick={() => place(ctx, v.deckIndex, null)}>
              Remove from deck
            </button>
          ) : (
            <button type="button" class="btn" onClick={addToDeck}>
              Add to deck
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function CardsPanel() {
  const ctx = useCtx();
  const [sel, setSel] = useState<string | null>(null);
  const [selSlot, setSelSlot] = useState<number | null>(null);
  if (!ctx) return null;
  const col = ctx.a.cards;
  const views = cardViews(col);
  const owned = views.filter((v) => v.owned).length;
  const selView = views.find((v) => v.def.id === sel) ?? null;
  const sets = cardSetProgress(col);
  const placeFirst = (id: string) => {
    const target = selSlot ?? col.deck.findIndex((d) => !d);
    if (target < 0) {
      notify('Your deck is full — right-click a deck card to remove it first.', '#e5a24d');
      return;
    }
    place(ctx, target, id);
    setSelSlot(null);
  };
  return (
    <Window title="Cards" width={1120} height={750} className="bwin bwin--cards">
      <div class="bcd" {...keyTrap}>
        <div class="bcd-left">
          <div class="bcd-summary">
            <span class="section-h">Card Book</span>
            <span>
              Collected <b>{owned}</b> / {views.length}
            </span>
            <span {...tip(() => <div class="btt-note">Every distinct card you own adds a little Max HP to all your characters.</div>)}>
              Collection bonus <b class="bcd-green">Max HP +{fmtPct(collectionHpBonus(col))}</b>
            </span>
          </div>
          <Collection views={views} sel={sel} setSel={setSel} onPlace={placeFirst} />
          <Detail ctx={ctx} v={selView} />
        </div>
        <div class="bcd-right">
          <Deck ctx={ctx} selCard={selView} selSlot={selSlot} setSelSlot={setSelSlot} setSelCard={setSel} />
          <div class="bcd-sets">
            <div class="section-h">Set Bonuses</div>
            {sets.map((s) => {
              const any = s.bonuses.some((b) => b.active);
              return (
                <div key={s.id} class={`bcd-set ${s.pieces ? '' : 'is-idle'} ${any ? 'is-on' : ''}`}>
                  <div class="bcd-set-h">
                    <span>{s.name}</span>
                    <small>
                      {s.pieces} card{s.pieces === 1 ? '' : 's'} · awakening {s.awaken}
                    </small>
                  </div>
                  {s.bonuses.map((b, i) => (
                    <div key={i} class={`bcd-set-l ${b.active ? 'is-on' : ''}`}>
                      <span>
                        {b.pieces} cards{b.awaken ? ` · ${b.awaken} aw.` : ''}
                      </span>
                      {b.text}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Window>
  );
}
